import { useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useAsync, useToast } from '../lib/store';
import type { Badge as BadgeData, BadgeCabinet, BadgeCategory } from '../lib/types';
import { Badge } from '../components/badges/Badge';
import { Bar, Button, EmptyState, ErrorState, Modal, Panel } from '../components/ui';
import { formatNumber } from '../lib/format';

/**
 * The Badge collection.
 *
 * Unearned badges are shown, greyed but complete — same shape, same emblem,
 * same ladder. A collection page that only lists what you have is a receipt;
 * one that shows the empty slots is the reason to keep playing, which is the
 * entire brief for this system.
 */

interface BadgesResponse {
  badges: BadgeData[];
  categories: BadgeCategory[];
  cabinet: BadgeCabinet;
  showcaseSlots: number;
}

type Status = 'all' | 'earned' | 'in-progress' | 'mastered' | 'unearned';
type Sort = 'default' | 'tier' | 'recent' | 'progress' | 'name' | 'score';

export function BadgesPage() {
  const [search, setSearch] = useState('');
  // Two independent checkboxes rather than one dropdown. "Earned" and "Not
  // started" are the only two states every badge is in, they are what a player
  // actually wants to toggle, and a checkbox says "both at once is normal" —
  // which a select cannot, because a select forces one answer.
  const [showEarned, setShowEarned] = useState(true);
  const [showUnearned, setShowUnearned] = useState(true);
  const [masteredOnly, setMasteredOnly] = useState(false);
  const [sort, setSort] = useState<Sort>('default');
  const [category, setCategory] = useState<string>('');
  const [detail, setDetail] = useState<BadgeData | null>(null);
  const [pinning, setPinning] = useState(false);

  // The server still owns filtering, so the checkbox pair is mapped onto the
  // status it already understands. Neither box ticked is a real answer — it
  // means "show me nothing" — and is handled without a pointless round trip.
  const nothingChecked = !showEarned && !showUnearned;
  const status: Status = masteredOnly
    ? 'mastered'
    : showEarned && showUnearned
      ? 'all'
      : showEarned
        ? 'earned'
        : 'unearned';

  const state = useAsync<BadgesResponse>(
    () =>
      api.get('/badges', {
        search: search || undefined,
        status,
        sort,
        category: category || undefined,
      }),
    [search, status, sort, category],
  );

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const badges = state.data?.badges ?? [];
  const categories = state.data?.categories ?? [];
  const cabinet = state.data?.cabinet;

  // Grouped by category unless the player has asked for an explicit ordering,
  // in which case a flat list is the whole point of having asked.
  const grouped = sort === 'default';

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Badges</h1>
          <div className="page-head__sub">
            {cabinet
              ? `${cabinet.earnedCount} of ${cabinet.totalCount} started · ${formatNumber(cabinet.totalScore)} Badge Score`
              : 'Mastery, measured in years.'}
          </div>
        </div>
        {cabinet && (
          <Button onClick={() => setPinning(true)} disabled={cabinet.earnedCount === 0}>
            ★ Choose featured
          </Button>
        )}
      </div>

      {cabinet && <CabinetSummary cabinet={cabinet} />}

      <Panel raised={false}>
        <div className="stack-sm">
          <div className="row row--wrap">
            <input
              className="input"
              style={{ flex: 1, minWidth: 180 }}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search badges..."
              aria-label="Search badges"
            />
            <select
              className="select"
              value={sort}
              onChange={(event) => setSort(event.target.value as Sort)}
              aria-label="Sort badges"
            >
              <option value="default">By category</option>
              <option value="tier">Highest tier</option>
              <option value="recent">Recently earned</option>
              <option value="progress">Closest to done</option>
              <option value="score">Badge Score</option>
              <option value="name">Name</option>
            </select>
          </div>

          <div className="row row--wrap">
            <label className="filter-check">
              <input
                type="checkbox"
                checked={showEarned}
                disabled={masteredOnly}
                onChange={(event) => setShowEarned(event.target.checked)}
              />
              Earned
            </label>
            <label className="filter-check">
              <input
                type="checkbox"
                checked={showUnearned}
                disabled={masteredOnly}
                onChange={(event) => setShowUnearned(event.target.checked)}
              />
              Not started
            </label>
            {/* Mastered is a subset of earned, not a third state, so it
                overrides rather than sitting alongside — two checkboxes that
                silently contradict each other is worse than one that wins. */}
            <label className="filter-check">
              <input
                type="checkbox"
                checked={masteredOnly}
                onChange={(event) => setMasteredOnly(event.target.checked)}
              />
              Mastered only
            </label>
          </div>

          <div className="row row--wrap">
            <button
              type="button"
              className={`chip ${category === '' ? 'chip--on' : ''}`}
              onClick={() => setCategory('')}
            >
              All
            </button>
            {categories.map((entry) => (
              <button
                key={entry.key}
                type="button"
                className={`chip ${category === entry.key ? 'chip--on' : ''}`}
                onClick={() => setCategory(entry.key === category ? '' : entry.key)}
                title={entry.description}
              >
                {entry.name}
              </button>
            ))}
          </div>
        </div>
      </Panel>

      {nothingChecked ? (
        <EmptyState art="◈" title="Nothing selected">
          Tick Earned or Not started to see your badges.
        </EmptyState>
      ) : state.loading ? (
        <Panel>
          <div className="badge-grid">
            {Array.from({ length: 8 }, (_, index) => (
              <div key={index} className="pxwell skeleton" style={{ height: 110 }} />
            ))}
          </div>
        </Panel>
      ) : badges.length === 0 ? (
        <EmptyState art="◈" title="No badges match">Try a different filter.</EmptyState>
      ) : grouped ? (
        categories
          .filter((entry) => badges.some((badge) => badge.categoryKey === entry.key))
          .map((entry) => (
            <Panel key={entry.key} title={entry.name} hint={entry.description}>
              <BadgeGrid
                badges={badges.filter((badge) => badge.categoryKey === entry.key)}
                onSelect={setDetail}
              />
            </Panel>
          ))
      ) : (
        <Panel>
          <BadgeGrid badges={badges} onSelect={setDetail} />
        </Panel>
      )}

      {detail && <BadgeDetail badge={detail} onClose={() => setDetail(null)} />}
      {pinning && cabinet && (
        <PinPicker
          badges={badges.filter((badge) => badge.tier > 0)}
          current={cabinet.featured.map((badge) => badge.key)}
          slots={state.data?.showcaseSlots ?? 6}
          onClose={() => setPinning(false)}
          onSaved={() => {
            setPinning(false);
            state.reload();
          }}
        />
      )}
    </div>
  );
}

function BadgeGrid({ badges, onSelect }: { badges: BadgeData[]; onSelect: (badge: BadgeData) => void }) {
  return (
    <div className="badge-grid">
      {badges.map((badge, index) => (
        <button
          key={badge.key}
          type="button"
          className={`pxwell badge-card ${badge.tier > 0 ? 'badge-card--earned' : ''}`}
          onClick={() => onSelect(badge)}
          // Staggered so a grid breathes as a crowd rather than in lockstep.
          // Prime-ish step and a modulo keep the cycle from looking like a wave.
          style={{ ['--badge-delay' as string]: `${((index * 370) % 3000) / 1000}s` }}
        >
          <Badge badge={badge} size={64} />
          <div className="badge-card__body">
            <div className="badge-card__name">{badge.name}</div>
            <div className="badge-card__tier">
              {badge.tier > 0 ? badge.tierName : 'Not started'}
              {badge.isTopTier && ' ✦'}
            </div>
            <div style={{ marginTop: 4 }}>
              <Bar ratio={badge.ratio} variant={badge.isTopTier ? 'ready' : 'exp'} />
            </div>
            <div className="tiny muted" style={{ marginTop: 2 }}>
              {badge.nextGoal === null
                ? 'Complete'
                : `${formatNumber(badge.progress)} / ${formatNumber(badge.nextGoal)}`}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

/** The full ladder for one badge — every rung, earned or not, with its reward. */
function BadgeDetail({ badge, onClose }: { badge: BadgeData; onClose: () => void }) {
  return (
    <Modal onClose={onClose} labelledBy="badge-detail-title">
      <div className="stack">
        <div className="row" style={{ gap: 'var(--gap)' }}>
          <Badge badge={badge} size={112} />
          <div style={{ minWidth: 0 }}>
            <h2 id="badge-detail-title" className="panel__title" style={{ margin: 0 }}>
              {badge.name}
            </h2>
            <div className="badge-card__tier">
              {badge.tier > 0 ? `${badge.tierName} tier` : 'Not started'}
              {badge.score > 0 && ` · ${formatNumber(badge.score)} score`}
            </div>
            <p className="tiny muted" style={{ marginTop: 6 }}>
              {badge.description}
            </p>
          </div>
        </div>

        <div className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
          <div className="row row--between tiny">
            <span>{badge.nextGoal === null ? 'Fully mastered' : `Next: ${badge.tiers.find((t) => !t.earned)?.name}`}</span>
            <span className="mono">
              {formatNumber(badge.progress)}
              {badge.nextGoal !== null && ` / ${formatNumber(badge.nextGoal)}`}
            </span>
          </div>
          <div style={{ marginTop: 6 }}>
            <Bar ratio={badge.ratio} tall variant={badge.isTopTier ? 'ready' : 'exp'} />
          </div>
        </div>

        <div className="stack-sm">
          {badge.tiers.map((rung) => (
            <div
              key={rung.tier}
              className="pxwell row row--between"
              style={{
                padding: 'var(--gap-sm)',
                gap: 'var(--gap-sm)',
                background: rung.earned ? 'var(--paper-warm)' : undefined,
                opacity: rung.earned ? 1 : 0.75,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="mono tiny">
                  {rung.earned ? '✓ ' : ''}
                  {rung.name}
                </div>
                <div className="tiny muted">
                  {formatNumber(rung.goal)} · {rung.score} score
                  {rung.rewardTitle && ` · Title: ${rung.rewardTitle}`}
                  {rung.rewardFrame && ' · Profile frame'}
                </div>
              </div>
              {(rung.rewardCoins > 0 || rung.rewardTokens > 0) && (
                <div className="tiny mono" style={{ whiteSpace: 'nowrap' }}>
                  {rung.rewardCoins > 0 && `${formatNumber(rung.rewardCoins)}c`}
                  {rung.rewardCoins > 0 && rung.rewardTokens > 0 && ' · '}
                  {rung.rewardTokens > 0 && `${formatNumber(rung.rewardTokens)}t`}
                </div>
              )}
            </div>
          ))}
        </div>

        <Button onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
}

/**
 * Choosing which badges to pin.
 *
 * Sends the whole ordered set rather than one add or remove at a time — the
 * server replaces it atomically, so there is no half-applied reorder and no
 * slot that can end up holding two badges.
 */
function PinPicker({
  badges,
  current,
  slots,
  onClose,
  onSaved,
}: {
  badges: BadgeData[];
  current: string[];
  slots: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { push } = useToast();
  const [picked, setPicked] = useState<string[]>(current);
  const [saving, setSaving] = useState(false);

  const byKey = useMemo(() => new Map(badges.map((badge) => [badge.key, badge])), [badges]);

  function toggle(key: string) {
    setPicked((chosen) =>
      chosen.includes(key)
        ? chosen.filter((entry) => entry !== key)
        : chosen.length >= slots
          ? chosen
          : [...chosen, key],
    );
  }

  async function save() {
    setSaving(true);
    try {
      await api.put('/badges/showcase', { badgeKeys: picked });
      push('success', 'Featured badges updated.');
      onSaved();
    } catch {
      push('error', 'Could not save your featured badges.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} wide labelledBy="pin-title">
      <div className="stack">
        <h2 id="pin-title" className="panel__title" style={{ margin: 0 }}>
          Featured badges
        </h2>
        <p className="tiny muted" style={{ margin: 0 }}>
          Pick up to {slots}. These sit at the top of your profile, in the order you choose them.
        </p>

        <div className="cabinet__pins">
          {picked.map((key) => {
            const badge = byKey.get(key);
            if (!badge) return null;
            return (
              <button
                key={key}
                type="button"
                className="cabinet__pin linkbtn"
                onClick={() => toggle(key)}
                title={`Remove ${badge.name}`}
                style={{ textDecoration: 'none' }}
              >
                <Badge badge={badge} size={64} still />
                <span className="cabinet__pin-name">{badge.name}</span>
              </button>
            );
          })}
          {Array.from({ length: Math.max(0, slots - picked.length) }, (_, index) => (
            <div key={`slot-${index}`} className="cabinet__pin">
              <div className="cabinet__slot">+</div>
            </div>
          ))}
        </div>

        <div className="badge-grid" style={{ maxHeight: 320, overflowY: 'auto' }}>
          {badges.map((badge) => (
            <button
              key={badge.key}
              type="button"
              className="pxwell badge-card"
              onClick={() => toggle(badge.key)}
              style={{
                outline: picked.includes(badge.key) ? '3px solid var(--leaf)' : 'none',
                outlineOffset: 2,
              }}
            >
              <Badge badge={badge} size={48} still />
              <div className="badge-card__body">
                <div className="badge-card__name">{badge.name}</div>
                <div className="badge-card__tier">{badge.tierName}</div>
              </div>
            </button>
          ))}
        </div>

        {badges.length === 0 && (
          <EmptyState art="◈" title="No badges yet">Earn a tier and it will show up here.</EmptyState>
        )}

        <div className="row row--between">
          <button type="button" className="linkbtn tiny" onClick={onClose}>
            Cancel
          </button>
          <Button variant="go" onClick={() => void save()} loading={saving}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** The headline numbers, repeated from the profile so the page has a summary. */
function CabinetSummary({ cabinet }: { cabinet: BadgeCabinet }) {
  return (
    <Panel raised={false}>
      <div className="row row--wrap" style={{ gap: 'var(--gap)' }}>
        <div>
          <div className="cabinet__score">{formatNumber(cabinet.totalScore)}</div>
          <div className="tiny muted">Badge Score</div>
        </div>
        <div>
          <div className="cabinet__score">{Math.round(cabinet.completion * 100)}%</div>
          <div className="tiny muted">Tiers earned</div>
        </div>
        <div>
          <div className="cabinet__score">{cabinet.topTierCount}</div>
          <div className="tiny muted">Mastered</div>
        </div>
        {cabinet.highest && (
          <div className="row" style={{ marginLeft: 'auto', gap: 'var(--gap-sm)' }}>
            <Badge badge={cabinet.highest} size={56} />
            <div>
              <div className="tiny muted">Highest</div>
              <div className="mono tiny">{cabinet.highest.name}</div>
              <div className="badge-card__tier">{cabinet.highest.tierName}</div>
            </div>
          </div>
        )}
      </div>
      <div style={{ marginTop: 'var(--gap-sm)' }}>
        <Bar ratio={cabinet.completion} tall variant="ready" />
      </div>
    </Panel>
  );
}
